import React, { FC, useState, useCallback, ReactNode } from "react";

import classnames from "classnames";

import { StyledAccordion } from "./Styles";

export interface IAccordion {
  /** Children type of node or string */
  children?: ReactNode;
  /** ClassName */
  className?: string;
  /** Could all items be collapsed */
  allowZeroExpanded?: boolean;
  /** Could more than one item be expanded */
  allowMultipleExpanded?: boolean;
  /** Callback function that will be called with id of the accordion item that was clicked */
  onChange?: (itemId: string) => void;
  /** List of item id's that are expanded on initial render */
  preExpanded?: string[];
  /** Do not show the toggle icon in the AccordionItemHead component */
  disableToggleIcon?: boolean;
  /** Custom toggle icon to replace the default one */
  customToggleIcon?: ReactNode;
}

export interface IAccordionContextType {
  expandedItems: string[];
  toggleExpanded: (itemId: string) => void;
  disableToggleIcon: boolean;
  customToggleIcon: ReactNode;
}

export const AccordionContext =
  React.createContext<IAccordionContextType | null>(null);

export const Accordion: FC<IAccordion> = ({
  children,
  className,
  allowMultipleExpanded = false,
  allowZeroExpanded = false,
  onChange,
  preExpanded = [],
  disableToggleIcon = false,
  customToggleIcon,
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>(preExpanded);

  const toggleExpandedCallback = useCallback(
    (id: string) => {
      setExpandedItems((prevExpanded: string[]) => {
        if (prevExpanded.indexOf(id) > -1) {
          if (!allowZeroExpanded && prevExpanded.length === 1) {
            return prevExpanded;
          }
          return prevExpanded.filter((item: string) => {
            return item !== id;
          });
        } else {
          if (!allowMultipleExpanded) {
            return [id];
          }
          return [...prevExpanded, id];
        }
      });
      if (onChange) {
        onChange(id);
      }
    },
    [allowMultipleExpanded, allowZeroExpanded, onChange]
  );

  return (
    <AccordionContext.Provider
      value={{
        expandedItems,
        toggleExpanded: toggleExpandedCallback,
        disableToggleIcon,
        customToggleIcon,
      }}
    >
      <StyledAccordion className={classnames("accordion", className)}>
        {children}
      </StyledAccordion>
    </AccordionContext.Provider>
  );
};

Accordion.displayName = "Accordion";
